import { getLadderStatus } from "../ladder-player/utils/ladder-progress";
import type { Ladder, LadderProgress } from "../ladder-player/types";

// The shapes below are the published learning index and awards file, cut down
// to what earning a certificate depends on. The CMS builds the same shapes
// from its own documents, so both apps resolve awards with this one code.

export type AwardRule =
  | "career_climb_list"
  | "all_climbs_in_course"
  | "all_climbs_in_plan";

export interface AwardPlanRef {
  code: string;
  id?: string;
}

export interface AwardRuleFields {
  awardRule?: string | null;
  awardType?: string | null;
  name?: string | null;
  title?: string | null;
  productPlan?: AwardPlanRef[] | null;
  requiredClimbs?: Array<string | number> | null;
  requiredCourses?: Array<string | number> | null;
  // Published as { code, id }. Older awards files carry a bare plan id, which
  // can't be matched against climbs, so it falls back like an unset plan.
  requiredPlan?: AwardPlanRef | string | null;
}

export interface AwardClimb {
  id: string | number;
  productPlan?: Array<{ code: string }> | null;
  ladders?: Array<Ladder | { id: string | number }> | null;
}

export interface AwardCourse {
  id: string | number;
  climbs?: Array<{ id?: string | number | null }> | null;
}

/**
 * The plan whose climbs an `all_climbs_in_plan` award requires: its
 * `requiredPlan`, or for awards that never set one, the first plan that can
 * see the award.
 */
const getRequiredPlanCode = (award: AwardRuleFields): string | null => {
  const required = award.requiredPlan;
  if (required && typeof required === "object" && required.code) {
    return required.code;
  }
  return award.productPlan?.[0]?.code ?? null;
};

const getAllClimbsInCourses = (
  courseIds: Array<string | number> | null | undefined,
  courses: AwardCourse[],
): string[] => {
  if (!courseIds?.length || !courses?.length) return [];
  return courseIds.flatMap((courseId) => {
    const course = courses.find((c) => String(c.id) === String(courseId));
    if (!course?.climbs) return [];
    return course.climbs
      .map((climb) => climb.id)
      .filter((id): id is string | number => id != null && id !== "")
      .map(String);
  });
};

const getAllClimbsInPlan = (
  planCode: string | null,
  allCareerClimbs: AwardClimb[],
): string[] => {
  if (!planCode) return [];
  return allCareerClimbs
    .filter((climb) => climb.productPlan?.some((p) => p.code === planCode))
    .map((climb) => String(climb.id));
};

// Returns all the climb ID's that match the award type
export const getClimbsForAward = (
  award: AwardRuleFields,
  allCareerClimbs: AwardClimb[],
  courses: AwardCourse[] = [],
): string[] => {
  switch (award.awardRule) {
    case "all_climbs_in_plan":
      return getAllClimbsInPlan(getRequiredPlanCode(award), allCareerClimbs);
    case "career_climb_list":
      return (award.requiredClimbs || []).map(String);
    case "all_climbs_in_course":
      return getAllClimbsInCourses(award.requiredCourses, courses);
    default:
      console.error("Unknown award rule ", award.awardRule);
      return [];
  }
};

export const hasCompletedClimb = (
  climbId: string | number,
  allCareerClimbs: AwardClimb[],
  ladderProgress: LadderProgress[] | null | undefined,
): boolean => {
  const careerClimb = allCareerClimbs.find(
    (climb) => String(climb.id) === String(climbId),
  );
  if (!careerClimb) return false;
  if (!ladderProgress) return false;
  return (careerClimb.ladders || []).every(
    (ladder) =>
      getLadderStatus(ladder as Ladder, ladderProgress) === "COMPLETED",
  );
};

const checkAwardCompletion = (
  award: AwardRuleFields,
  allCareerClimbs: AwardClimb[],
  ladderProgress: LadderProgress[] | null | undefined,
  courses: AwardCourse[] = [],
): boolean => {
  const climbsForAward = getClimbsForAward(award, allCareerClimbs, courses);

  // If no climbs are required for this award, it should not be marked as complete
  if (climbsForAward.length === 0) {
    return false;
  }

  return climbsForAward.every((climbId) =>
    hasCompletedClimb(climbId, allCareerClimbs, ladderProgress),
  );
};

export const getCompletedAwards = <T extends AwardRuleFields>(
  awards: T[],
  allCareerClimbs: AwardClimb[],
  ladderProgress: LadderProgress[] | null | undefined,
  courses: AwardCourse[] = [],
): T[] =>
  awards.filter((award) =>
    checkAwardCompletion(award, allCareerClimbs, ladderProgress, courses),
  );

// Award types that read as a certificate to a climber. POK credentials are
// certificates too — the certificate report already groups them together.
const CERTIFICATE_AWARD_TYPES = ["certificate", "pok"];

const isCertificateAward = (
  award: AwardRuleFields | null | undefined,
): boolean => CERTIFICATE_AWARD_TYPES.includes(award?.awardType ?? "");

// `name` is the CMS's internal label; `title` is what climbers are shown.
export const certificateLabel = (
  award: AwardRuleFields | null | undefined,
): string => award?.title || award?.name || "";

/**
 * Certificates a climber can work toward on a page showing `visibleClimbs`,
 * paired with the climbs that count toward each.
 *
 * Contributing climbs come from `getClimbsForAward`, the same resolver that
 * decides when the award is actually earned, so a filter built on this can
 * never disagree with what grants the certificate. Certificates with nothing to
 * contribute here are dropped: an option that filters to an empty list is the
 * bug, not the fix.
 */
export const getCertificatesWithClimbs = <T extends AwardRuleFields>(
  awards: T[] | null | undefined,
  visibleClimbs: Array<{ id: string | number }> | null | undefined,
  allCareerClimbs: AwardClimb[] | null | undefined,
  courses: AwardCourse[] = [],
): Array<{ award: T; climbIds: Set<string> }> => {
  const visibleIds = new Set((visibleClimbs || []).map((c) => String(c.id)));

  return (awards || [])
    .filter(isCertificateAward)
    .map((award) => {
      const climbIds = new Set(
        getClimbsForAward(award, allCareerClimbs || [], courses).filter((id) =>
          visibleIds.has(id),
        ),
      );
      return { award, climbIds };
    })
    .filter(({ climbIds }) => climbIds.size > 0)
    .sort((a, b) =>
      certificateLabel(a.award).localeCompare(certificateLabel(b.award)),
    );
};

export interface AwardExplanation {
  rule: AwardRule | null;
  // The plan an `all_climbs_in_plan` award draws its climbs from.
  planCode: string | null;
  // Unique climb ids a learner must finish, in rule order.
  climbIds: string[];
  // Required climbs or courses missing from the content passed in. For a
  // Program's content that means learners can't see them, so can't earn this.
  missingClimbIds: string[];
  missingCourseIds: string[];
  // Unique ladders across the required climbs that were found.
  ladderCount: number;
  earnable: boolean;
}

/**
 * What it takes to earn `award`, resolved against the content a learner can
 * see. A certificate is earned when every ladder in every required climb is
 * complete; there is no partial credit, ordering or deadline.
 */
export const explainAward = (
  award: AwardRuleFields,
  climbs: AwardClimb[],
  courses: AwardCourse[] = [],
): AwardExplanation => {
  const rule = (
    [
      "career_climb_list",
      "all_climbs_in_course",
      "all_climbs_in_plan",
    ].includes(award.awardRule ?? "")
      ? award.awardRule
      : null
  ) as AwardRule | null;
  const climbIds = rule
    ? [...new Set(getClimbsForAward(award, climbs, courses))]
    : [];
  const climbsById = new Map(climbs.map((c) => [String(c.id), c]));
  const courseIds = new Set(courses.map((c) => String(c.id)));

  const missingClimbIds = climbIds.filter((id) => !climbsById.has(id));
  const missingCourseIds =
    rule === "all_climbs_in_course"
      ? (award.requiredCourses || [])
          .map(String)
          .filter((id) => !courseIds.has(id))
      : [];

  const ladderIds = new Set<string>();
  for (const id of climbIds) {
    for (const ladder of climbsById.get(id)?.ladders || []) {
      ladderIds.add(String(ladder.id));
    }
  }

  return {
    rule,
    planCode: rule === "all_climbs_in_plan" ? getRequiredPlanCode(award) : null,
    climbIds,
    missingClimbIds,
    missingCourseIds,
    ladderCount: ladderIds.size,
    earnable:
      climbIds.length > 0 &&
      missingClimbIds.length === 0 &&
      missingCourseIds.length === 0,
  };
};
